import { Immutable } from "immer";
import React, { useEffect, useRef } from "react";
import { t } from "ttag";

import type { FeaturedProductsCardBlock as Value } from "@reactivated";

import { constants } from "@reactivated/constants";

import { RichTextBlock } from "@thelabnyc/thelabui/src/components/RichText";
import { richT } from "@thelabnyc/thelabui/src/utils/richText";
import { concatClassNames } from "@thelabnyc/thelabui/src/utils/styles";

import { pushPrebuiltGA4Event } from "../../utils/analytics";
import { money } from "../../utils/format";
import { usePageSetting, useShouldShowPricing } from "../../utils/settings";
import { hasRichTextContent } from "../../utils/streamfieldContent";
import { isElementVisible } from "../../utils/visibility";
import { Flair } from "../Flair";
import { getGlobalSpacingClasses } from "../_spacing/globalSpacing";

const AppSlug = constants["tsicommon.types.AppSlug"];

type IProps = Immutable<{
    value: Value;
}>;

type ProductCard = NonNullable<Value["product_cards"]>[number];
type ProductInfo = NonNullable<ProductCard["product_info"]>;

/**
 * Faithful client port of the legacy server-rendered
 * ``tsicommon_cms/blocks/featured-products-card-block.html`` (#33285/#33420).
 *
 * Every server-computed value the legacy template rendered is SERIALIZED by the
 * block (see ``featured_products_card.py``):
 *
 *   - ``card.product_info``: title/display_title, PDP link
 *     (``product_pdp_link``), review aggregate (product -> parent fallback
 *     branch), the "Starting at" gate
 *     (``not product.parent and product.children.count != 0``), the fallback
 *     image URL, and the ``purchase_info_for_product`` price (``None`` unless
 *     ``price.exists`` -- so its presence is the client equivalent of the
 *     legacy gate; ``useShouldShowPricing`` mirrors
 *     ``site_settings.should_show_pricing``).
 *   - ``value.financing_url``: ``{% slugurl 'finance-your-purchase' %}``.
 *   - GA4: BOTH events arrive PRE-BUILT (same ``tsicommon_tracker_tags``
 *     builders the legacy tags used) and are pushed verbatim --
 *     ``value.view_item_list_event`` on mount (gated on element visibility,
 *     mirroring the ``push_to_datalayer.html`` ``_isVisible`` walk) and
 *     ``card.select_item_event`` onclick of the legacy "Shop Now" anchor.
 *     Server-side DNT requests serialize null events (legacy parity).
 *
 * Star ratings reproduce the ``tsicommon_cms/molecules/star-rating.html``
 * markup (``.star-rating > .star-rating__inner-container > .star-rating__stars``
 * + ``.star-rating__number`` with the "{N}[+] Reviews" text and the
 * 0.25/0.75 half-star thresholds). The star SVGs are the 50x50 set shared by
 * the sites' ``svg-django`` sprites (sealy's *outline* star sprite is a 24x24
 * variant; the 50x50 outline is visually equivalent at rendered size and the
 * outline only appears on low-rated products).
 *
 * Like the legacy ``{% wagtail_image %}``/``{% django_image %}`` tags under
 * the puppeteer UA, images render as plain eager ``<img>`` tags using the
 * serialized ``width-1000`` rendition (AwardColumns/FeaturedProductLines
 * precedent: the lazysizes placeholder markup is not reproduced).
 */

// --- Star rating (molecule parity) -------------------------------------------

const STAR_PATH =
    "M49.2 21.1 38.3 31.7l2.6 15v.6c0 .8-.4 1.5-1.2 1.5-.4 0-.8-.2-1.2-.4l-13.5-7-13.5 7.1c-.4.2-.8.4-1.2.4-.9 0-1.3-.7-1.3-1.5 0-.2 0-.4.1-.6l2.6-15L.8 21.1c-.4-.4-.8-.9-.8-1.5 0-.9.9-1.3 1.7-1.4L16.8 16l6.8-13.7c.3-.6.8-1.2 1.5-1.2s1.2.7 1.5 1.2L33.4 16l15.1 2.2c.7.1 1.7.5 1.7 1.4-.2.6-.6 1.1-1 1.5";
const STAR_HALF_OUTLINE_PATH =
    "m38.3 31.7 2.6 15c.2 1.3-.3 2.1-1.2 2.1-.3 0-.7-.1-1.2-.4l-13.5-7-13.5 7.1c-.5.2-.9.4-1.2.4-.9 0-1.4-.8-1.2-2.1l2.6-15L.8 21.1c-1.3-1.3-.9-2.6.9-2.8l15.1-2.2 6.8-13.7q.6-1.2 1.5-1.2c.5 0 1.1.4 1.5 1.2l6.8 13.7 15.1 2.2c1.8.3 2.2 1.5.9 2.8zm5.1-10.3-10.7-1.6-2-.3-.9-1.8L25 8.1V37l1.8.9 9.6 5.1-1.8-10.7-.4-2 1.5-1.4z";
const STAR_OUTLINE_PATH =
    "M49.2 21.1 38.3 31.7l2.6 15v.6c0 .8-.4 1.5-1.2 1.5-.4 0-.8-.2-1.2-.4l-13.5-7-13.5 7.1c-.4.2-.8.4-1.2.4-.9 0-1.3-.7-1.3-1.5 0-.2 0-.4.1-.6l2.6-15-11-10.7c-.3-.4-.7-.9-.7-1.5 0-.9.9-1.3 1.7-1.4L16.8 16l6.8-13.7c.3-.6.8-1.2 1.5-1.2s1.2.7 1.5 1.2L33.4 16l15.1 2.2c.7.1 1.7.5 1.7 1.4-.2.6-.6 1.1-1 1.5m-5.8.3-12.7-1.9L25 8.1l-5.7 11.5-12.7 1.8 9.2 8.9L13.6 43 25 37l11.3 6-2.2-12.6z";

const StarSVG = (props: { path: string }): React.JSX.Element => (
    <svg
        className="star-rating__star"
        role="img"
        xmlns="http://www.w3.org/2000/svg"
        xmlSpace="preserve"
        width="50"
        height="50"
        viewBox="0 0 50 50"
    >
        <path d={props.path} />
    </svg>
);

const getStarPath = (rating: number, starIndex: number): string => {
    // EXACT molecule thresholds: full at >= i+0.75, half at >= i+0.25.
    if (rating >= starIndex + 0.75) {
        return STAR_PATH;
    }
    if (rating >= starIndex + 0.25) {
        return STAR_HALF_OUTLINE_PATH;
    }
    return STAR_OUTLINE_PATH;
};

/**
 * Markup-parity port of ``tsicommon_cms/molecules/star-rating.html`` (which
 * the legacy block included; NOT the ``sub-blocks/star-rating.html`` variant
 * RatingGraphic matches -- the molecule has no screen-reader label span and
 * supports the ``numbersWithPlus`` "{N}+ Reviews" form used by the
 * ``card-style--updated`` theme). The molecule's own ``APP_SLUG != tempurca``
 * gate is enforced by the caller (which already wraps the whole reviews
 * branch in the same condition).
 */
const MoleculeStarRating = (
    props: Immutable<{
        rating: number | null;
        number: number;
        numbersWithPlus: boolean;
    }>,
): React.JSX.Element => {
    const rating = props.rating ?? 0;
    return (
        <div className="star-rating ">
            <div className="star-rating__inner-container">
                <div className="star-rating__stars">
                    {[0, 1, 2, 3, 4].map((starIndex) => (
                        <StarSVG
                            key={starIndex}
                            path={getStarPath(rating, starIndex)}
                        />
                    ))}
                </div>
                {props.number ? (
                    <div className="star-rating__number">
                        {new Intl.NumberFormat().format(props.number)}
                        {props.numbersWithPlus ? "+" : ""} {t`Reviews`}
                    </div>
                ) : null}
            </div>
        </div>
    );
};

// --- Price ---------------------------------------------------------------------

type ProductPrice = NonNullable<ProductInfo["price"]>;

const StartingAtCopy = (): React.JSX.Element => (
    <>
        <span className="product-card__price--copy">{t`Starting at`}</span>{" "}
    </>
);

/**
 * Hand-render the legacy ``product-card__price`` markup (global classes the
 * block SCSS targets). Branch condition is the EXACT legacy expression:
 * ``not price.retail or price.retail == price.cosmetic_excl_tax``.
 */
const ProductCardPrice = (
    props: Immutable<{
        price: ProductPrice;
        showStartingAt: boolean;
        financingCopy: ProductCard["financing_copy"];
        financingUrl: string | null;
    }>,
): React.JSX.Element => {
    const price = props.price;
    const isPlainPrice =
        !price.retail || price.retail === price.cosmetic_excl_tax;
    const startingAt = props.showStartingAt ? <StartingAtCopy /> : null;
    const showFinancingLink =
        !!price.per_month && props.financingCopy !== "special";
    // Money/number values are the only interpolated placeholders; all prose
    // ("or", "/mo.", "for", "months", "(total payments of ...)") lives inside a
    // single t`` template so translators can reorder the whole sentence (legacy
    // single blocktrans parity). The underline wrappers, the <sup> footnote
    // markers and the <br/> are markup tags resolved by richT(), so the
    // rendered DOM matches the legacy template exactly.
    const perMonth = money(price.per_month);
    const termLength = price.per_month_term_length;
    const formattedPrice = money(price.cosmetic_excl_tax);
    return (
        <div className="product-card__price">
            {isPlainPrice ? (
                <span className="product-card__price--actual">
                    {startingAt}
                    {money(price.cosmetic_excl_tax)}
                </span>
            ) : (
                <>
                    <span className="product-card__price--retail">
                        <span className="ada-screenreader-only">
                            {t`Original Price:`}
                        </span>
                        {startingAt}
                        <span className="product-card__price--strikethrough">
                            {money(price.retail)}
                        </span>
                    </span>
                    <span className="product-card__price--highlight">
                        <span className="ada-screenreader-only">
                            {t`Current Price:`}
                        </span>
                        {money(price.cosmetic_excl_tax)}
                    </span>
                </>
            )}
            {showFinancingLink && (
                <a href={`${props.financingUrl ?? ""}#terms_and_conditions`}>
                    {richT(
                        t`or <permonth>${perMonth}/mo.</permonth><sup>2</sup> for <term>${termLength} months</term><sup>1</sup><br/><total>(total payments of ${formattedPrice})</total>`,
                        {
                            permonth: (children) => (
                                <span className="product-card__price--underline">
                                    {children}
                                </span>
                            ),
                            term: (children) => (
                                <span className="product-card__price--underline">
                                    {children}
                                </span>
                            ),
                            total: (children) => <span>{children}</span>,
                        },
                    )}
                </a>
            )}
        </div>
    );
};

// --- Card ------------------------------------------------------------------------

const ProductCardElem = (
    props: Immutable<{
        card: ProductCard;
        theme: Value["theme"];
        financingUrl: string | null;
    }>,
): React.JSX.Element => {
    const card = props.card;
    const info = card.product_info;
    const shouldShowPricing = useShouldShowPricing();
    const appSlug = usePageSetting("app_slug") || "";
    const ecomEnabled = !!usePageSetting("enable_ecom");
    const isUpdatedTheme = props.theme === "card-style--updated";

    const image = card.product_image;
    const imageSrc = image
        ? (image.renditions["width-1000"]?.url ?? image.url)
        : null;

    return (
        <div className="product-card" role="group" aria-label={t`item`}>
            {card.flair?.content && (
                <Flair
                    flair={{
                        style: card.flair.style,
                        content: card.flair.content,
                    }}
                />
            )}

            {hasRichTextContent(card.header_content) && (
                <RichTextBlock value={card.header_content ?? ""} />
            )}

            {image && imageSrc ? (
                <img
                    src={imageSrc}
                    alt={image.title}
                    loading="lazy"
                    decoding="async"
                />
            ) : info?.fallback_image_url ? (
                <img
                    src={info.fallback_image_url}
                    alt={info.display_title}
                    loading="lazy"
                    decoding="async"
                />
            ) : null}

            {info && (
                <>
                    <h3>{info.display_title}</h3>

                    {appSlug !== AppSlug.TEMPURCA &&
                        (info.review ? (
                            <a
                                title={t`Read reviews for ${info.title}`}
                                href={`${info.url}#reviews`}
                            >
                                <MoleculeStarRating
                                    rating={info.review.rating}
                                    number={info.review.num_reviews}
                                    numbersWithPlus={isUpdatedTheme}
                                />
                            </a>
                        ) : isUpdatedTheme ? (
                            <span>{t`NEW!`}</span>
                        ) : null)}
                </>
            )}

            {card.main_description != null && (
                <RichTextBlock value={card.main_description} />
            )}

            {info && (
                <>
                    {info.price && shouldShowPricing && (
                        <ProductCardPrice
                            price={info.price}
                            showStartingAt={info.show_starting_at}
                            financingCopy={card.financing_copy}
                            financingUrl={props.financingUrl}
                        />
                    )}

                    {!isUpdatedTheme && (
                        <a
                            className="button"
                            href={info.url}
                            onClick={() => {
                                // Legacy `select_item_tracking_attrs` onclick:
                                // push the PRE-BUILT select_item event verbatim
                                // (null when DNT or no product -- no-op).
                                pushPrebuiltGA4Event(card.select_item_event);
                            }}
                        >
                            {ecomEnabled ? t`Shop Now` : t`Explore`}
                        </a>
                    )}
                </>
            )}

            {card.financing_copy === "special" && (
                <a
                    className="product-card__price--special"
                    href={props.financingUrl ?? ""}
                >
                    {t`Special financing available`}
                </a>
            )}
            {card.footer_description != null && (
                <RichTextBlock value={card.footer_description} />
            )}
        </div>
    );
};

// --- Block ---------------------------------------------------------------------

export const FeaturedProductsCardBlock = (props: IProps): React.JSX.Element => {
    const v = props.value;
    const cards = v.product_cards ?? [];
    const sectionRef = useRef<HTMLElement | null>(null);
    const firedRef = useRef(false);

    // Fire the ``view_item_list`` impression exactly once on mount from the
    // PRE-BUILT server event (same ``_build_item_list_event`` builder as the
    // legacy ``{% track_view_item_list %}`` tag; null under DNT). Visibility
    // gate mirrors the legacy ``push_to_datalayer.html`` ``_isVisible`` walk
    // (e.g. ``responsive_visibility``-hidden blocks must not inflate GA4).
    useEffect(() => {
        if (firedRef.current) {
            return;
        }
        firedRef.current = true;
        if (!isElementVisible(sectionRef.current)) {
            return;
        }
        pushPrebuiltGA4Event(v.view_item_list_event);
        // Fire-on-mount only: the block value is static per page render.
    }, []);

    return (
        <section
            ref={sectionRef}
            className={concatClassNames([
                "featured-products-card-block",
                v.theme ?? "",
                v.responsive_visibility?.visibility ?? "",
                getGlobalSpacingClasses(v.spacing),
            ])}
        >
            <header>
                {v.header_content != null && (
                    <RichTextBlock value={v.header_content} />
                )}
            </header>
            <div>
                {cards.map((card, i) => (
                    <ProductCardElem
                        key={i}
                        card={card}
                        theme={v.theme}
                        financingUrl={v.financing_url}
                    />
                ))}
            </div>
            <footer>
                {v.footer_content != null && (
                    <RichTextBlock value={v.footer_content} />
                )}
            </footer>
        </section>
    );
};
